import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../lib/api';
import Breadcrumbs from '../components/Breadcrumbs';
import GroupedProofGallery from '../components/GroupedProofGallery';
import ReportBuilderDialog from '../components/ReportBuilderDialog';
import { StatCard } from '../components/metrics';
import { Users, Loader2, Megaphone, Layers, Images, ArrowRight, Building2 } from 'lucide-react';

export default function ClientDetail() {
  const { id } = useParams();
  const [client, setClient] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [proofs, setProofs] = useState(null);

  useEffect(() => {
    api.get('/users?role=client').then((r) => setClient(r.data.find((c) => c.id === id) || false));
    api.get('/campaigns').then((r) => setCampaigns(r.data)).catch(() => {});
    api.get(`/proofs?client_id=${id}&limit=1000`).then((r) => setProofs(r.data.proofs)).catch(() => setProofs([]));
  }, [id]);

  if (client === false) return <p className="text-muted-foreground">Client not found.</p>;
  if (!client || proofs === null) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;

  const mine = campaigns.filter((c) => c.client_user_id === id || c.client_name === client.name);
  const sites = mine.reduce((a, c) => a + (c.site_ids?.length || 0), 0);

  return (
    <div className="space-y-5">
      <Breadcrumbs items={[{ label: 'Clients', to: '/clients' }, { label: client.name }]} />
      <div className="flex items-center gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/15 text-primary"><Users className="h-7 w-7" /></div>
        <div className="flex-1">
          <p className="overline text-primary">Client</p>
          <h1 className="font-head text-3xl font-bold">{client.name}</h1>
          <p className="text-sm text-muted-foreground">{client.email}</p>
        </div>
        <ReportBuilderDialog scope={{ client_id: id }} groupByDefault="campaign" triggerLabel="Client Report" defaults={{ title: `${client.name} - Media Inspection Report`, client_name: client.name }} />
      </div>

      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Campaigns" value={mine.length} icon={Megaphone} tone="text-primary" />
        <StatCard label="Sites" value={sites} icon={Layers} tone="text-blue-400" />
        <StatCard label="Proof photos" value={proofs.length} icon={Images} tone="text-emerald-400" />
      </div>

      <div>
        <p className="font-head text-xl font-bold mb-2">Campaigns</p>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {mine.map((c) => (
            <Link key={c.id} to={`/campaigns/${c.id}`} data-testid={`client-campaign-card-${c.id}`} className="tile-press rounded-xl border-2 border-border bg-card p-4 hover:border-primary">
              <div className="flex items-center justify-between">
                <p className="font-head text-lg font-bold">{c.name}</p>
                {c.status === 'cancelled'
                  ? <span className="rounded-full bg-rose-500/20 px-2 py-0.5 text-[10px] uppercase text-rose-300">Cancelled</span>
                  : <span className="rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] uppercase text-emerald-300">Active</span>}
              </div>
              <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground"><Building2 className="h-3.5 w-3.5" /> {c.site_ids?.length || 0} sites</p>
              <p className="mt-1 font-mono text-[11px] text-muted-foreground">{c.start_date || '—'} → {c.end_date || '—'}</p>
              <span className="mt-2 flex items-center text-xs text-primary">View campaign <ArrowRight className="ml-1 h-3.5 w-3.5" /></span>
            </Link>
          ))}
          {!mine.length && <p className="text-sm text-muted-foreground">No campaigns linked to this client yet.</p>}
        </div>
      </div>

      <div>
        <p className="font-head text-xl font-bold mb-2">Proof Photos</p>
        <GroupedProofGallery proofs={proofs} allowedGroupings={['society_tower', 'campaign', 'society', 'lift', 'site_type', 'action', 'operator', 'inspection']} initialGroupBy="society_tower" emptyMessage="No proof photos captured for this client's campaigns yet." />
      </div>
    </div>
  );
}
